<!-- The company's view and the feature flags that apply to it. Flags of other views are kept when the
     view changes, so switching back restores them; the ones left on are listed. -->
<template>
  <div class="col-md-6">
    <label :for="viewSelectId" class="form-label fw-semibold"
      >Company View</label
    >
    <select
      :id="viewSelectId"
      v-model="dashboardView"
      class="form-select"
      data-testid="company-dashboard-view"
    >
      <option v-for="(label, view) in COMPANY_VIEWS" :key="view" :value="view">
        {{ label }}
      </option>
    </select>
    <small class="form-text text-muted">
      Picks the dashboard and which feature flags below apply.
    </small>
  </div>

  <div class="col-12">
    <label class="form-label fw-semibold mb-2">Company Feature Flags</label>
    <div
      v-for="group in visibleGroups"
      :key="group.id"
      class="mb-3"
      :data-testid="`flag-group-${group.id}`"
    >
      <div class="small text-uppercase text-muted fw-semibold mb-1">
        {{ group.label }}
      </div>
      <div class="row">
        <div
          v-for="flag in group.flags"
          :key="flag.key"
          class="col-md-4 col-lg-3 mb-2"
        >
          <div class="form-check">
            <input
              :id="`${idPrefix}-${flag.key}`"
              type="checkbox"
              class="form-check-input"
              :data-testid="`flag-${flag.key}`"
              :checked="featureFlags?.[flag.key] === true"
              @change="
                updateFeatureFlag(
                  flag.key,
                  ($event.target as HTMLInputElement).checked
                )
              "
            />
            <label class="form-check-label" :for="`${idPrefix}-${flag.key}`">
              <InfoTooltip :text="flag.description">{{
                flag.label
              }}</InfoTooltip>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="hiddenEnabledFlags.length > 0"
      class="alert alert-warning small py-2 mb-0"
      data-testid="hidden-enabled-flags"
    >
      Still on, but unused by this view:
      {{ hiddenEnabledFlagLabels }}.
      <button
        type="button"
        class="btn btn-link btn-sm p-0 align-baseline"
        @click="clearHiddenFlags"
      >
        Turn them off
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import {
  COMPANY_VIEWS,
  type CompanyFeatureFlagKey,
  type CompanyView,
  FEATURE_FLAG_GROUPS,
  FEATURE_FLAGS,
} from "@/constants";
import type { FeatureFlags } from "@/types";
import { computed, useId } from "vue";

const dashboardView = defineModel<CompanyView>("dashboardView", {
  required: true,
});
const featureFlags = defineModel<FeatureFlags | undefined>("featureFlags");

const viewSelectId = useId();
const idPrefix = useId();

const visibleGroups = computed(() =>
  FEATURE_FLAG_GROUPS.filter((group) =>
    (group.views as readonly CompanyView[]).includes(dashboardView.value)
  )
);

const hiddenEnabledFlags = computed(() => {
  const visibleFlags = new Set<CompanyFeatureFlagKey>(
    visibleGroups.value.flatMap((group) => group.flags.map((flag) => flag.key))
  );
  return (Object.keys(FEATURE_FLAGS) as CompanyFeatureFlagKey[]).filter(
    (flag) => !visibleFlags.has(flag) && featureFlags.value?.[flag] === true
  );
});
const hiddenEnabledFlagLabels = computed(() =>
  hiddenEnabledFlags.value.map((flag) => FEATURE_FLAGS[flag]).join(", ")
);

function updateFeatureFlag(flag: CompanyFeatureFlagKey, enabled: boolean) {
  featureFlags.value = { ...(featureFlags.value ?? {}), [flag]: enabled };
}

function clearHiddenFlags() {
  featureFlags.value = {
    ...(featureFlags.value ?? {}),
    ...Object.fromEntries(
      hiddenEnabledFlags.value.map((flag) => [flag, false])
    ),
  };
}
</script>
